import { attrToBool, BooleanAttr } from "common/helpers";
import { api, LightningElement, wire } from "lwc";
import type {
  CelebrationModalState,
  StreakData,
  DownloadMobileData,
} from "./types";
import { assignedElements } from "common/slot";
import flameGif from "../../common/assets/flame.gif";
import { getAssetsUrl } from "common/helpers";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import * as locales from "./locales";
import { LastPageCta } from "../pagedModal/types";
import { PointsData } from "../pointsCelebrationPage/types";
import { RankData } from "../rankUpCelebrationPage/types";
import { trackUserActivity, type UserActivityEvents } from "common/track";

export type { CelebrationModalState, StreakData };

export const supportedStreaksSocialMilestones = [
  1, 2, 4, 6, 8, 10, 20, 30, 40, 52,
];

export default class CelebrationModal
  extends LightningElement
  implements CelebrationModalState
{
  @api open: BooleanAttr = null;
  @api lastPageCta: LastPageCta | null = null;
  @api assistiveText: string | null = null;
  @api agentforce: BooleanAttr = null;
  @api streakData: StreakData | null = null;
  @api pointsData: PointsData | null = null;
  @api rankData: RankData | null = null;
  @api downloadMobileData: DownloadMobileData | null = null;

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = {};

  /**
   * This method exists because some React components include LWCs that
   * load data with API calls for pageLast slot. Sometimes those API calls
   * return empty content and if that happens, the modal needs to
   * be able to completely remove the pageLast slot from the UI.
   */
  @api
  removeLastPageSlot() {
    const slot = this.template.querySelector(
      `slot[name="pageLast"]`
    ) as HTMLSlotElement;

    if (!slot) return;

    const lastPageNodes = assignedElements(slot);

    (slot as HTMLElement).removeAttribute("slot");
    lastPageNodes.forEach((el) => {
      el.remove();
    });
  }

  private get labelOptions() {
    const duration = this.streakData?.duration;
    return {
      streakWeeks: {
        duration,
      },
      streakWeeksPlural: {
        duration,
      },
    };
  }

  renderedCallback() {
    this.setSlotContent("page1");
    this.setSlotContent("pageLast");
  }

  connectedCallback() {
    // Alwyas preload the Streak flame gif to ensure that the image exists
    // when the modal opens, assuming the user will see a Streak celebration.
    // This ensures that the page displays the animation without weird loading...
    this.addStreaksFlamePreloadLink();

    if (this.isOpen) {
      this.track("modal_opened");

      if (this.hasStreakData) {
        this.track("streak_celebration_modal_viewed");
      }
    }
  }

  private addStreaksFlamePreloadLink() {
    if (
      !document.querySelector(`link[rel="preload"][href="${this.streakIcon}"]`)
    ) {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = this.streakIcon;
      document.head.appendChild(link);
    }
  }

  private setSlotContent(slotName: string) {
    const slot = this.template.querySelector(
      `slot[name="${slotName}"]`
    ) as HTMLSlotElement;

    if (!slot) return;

    const assignedNodes = assignedElements(slot);

    const hasContent = assignedNodes.some(
      (node) =>
        node.nodeType === Node.ELEMENT_NODE ||
        (node.nodeType === Node.TEXT_NODE && node.textContent?.trim())
    );

    if (hasContent) {
      (slot as HTMLElement).setAttribute("slot", "pages");
    } else {
      (slot as HTMLElement).removeAttribute("slot");
    }
  }

  private get isOpen() {
    return attrToBool(this.open);
  }

  private get isAgentforce() {
    return attrToBool(this.agentforce);
  }

  private get hasStreakData() {
    return this.streakData !== null && this.streakData?.duration;
  }

  private get hasPointsData() {
    return !!this.pointsData?.points;
  }

  private get hasRankData() {
    return !!this.rankData?.rank;
  }

  private get hasDownloadMobileData() {
    return !!this.downloadMobileData?.show;
  }

  private get streakIcon() {
    return getAssetsUrl(flameGif);
  }

  private get durationTitle() {
    if (this.streakData?.duration == 1) {
      return this.labels.streakWeeks;
    } else {
      return this.labels.streakWeeksPlural;
    }
  }

  private get streakTitle() {
    switch (this.streakData?.duration) {
      case 1:
        return this.labels.title;
      case 2:
        return this.labels.titleWeek2;
      case 4:
        return this.labels.titleWeek4;
      case 6:
        return this.labels.titleWeek6;
      case 8:
        return this.labels.titleWeek8;
      case 10:
        return this.labels.titleWeek10;
      case 20:
        return this.labels.titleWeek20;
      case 30:
        return this.labels.titleWeek30;
      case 40:
        return this.labels.titleWeek40;
      case 52:
        return this.labels.titleWeek52;
      default:
        return this.labels.title;
    }
  }

  private get streakSubtitle() {
    switch (this.streakData?.duration) {
      case 1:
        return this.labels.subtitle;
      case 2:
        return this.labels.subtitleWeek2;
      case 4:
        return this.labels.subtitleWeek4;
      case 6:
        return this.labels.subtitleWeek6;
      case 8:
        return this.labels.subtitleWeek8;
      case 10:
        return this.labels.subtitleWeek10;
      case 20:
        return this.labels.subtitleWeek20;
      case 30:
        return this.labels.subtitleWeek30;
      case 40:
        return this.labels.subtitleWeek40;
      case 52:
        return this.labels.subtitleWeek52;
      default:
        return this.labels.subtitle;
    }
  }

  private track(eventName: keyof UserActivityEvents) {
    trackUserActivity(this, eventName, {
      pageLocation: "th-celebration-modal",
    });
  }

  private get renderStreaksSocialShare() {
    if (
      this.streakData?.duration &&
      supportedStreaksSocialMilestones.includes(this.streakData?.duration)
    ) {
      return true;
    }

    return false;
  }

  private get streakShareUrl() {
    if (this.streakData) {
      return `https://trailhead.salesforce.com/streaks/${this.streakData.duration}`;
    }

    return "";
  }
}
